import React, {
  createContext, useContext, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { useLocalStorage } from '@thd-olt-global/thd-storage-utils';
import { useMessageHistory } from '../../hooks/useMessageHistory';
import { useAssistantBot } from '../../hooks/useAssistantBot';
import { MESSAGE_ORIGIN_UNSPECIFIED, RETRY_MESSAGE } from './constants';
import { nrTrack } from '../../utils/assistant-utils';
import { getParentPagePath, getExperienceName } from '../../utils/assistant-context-utils';
import { getDefaultSession } from '../../utils/session-utils';

const AssistantContext = createContext({
  historyCleared: false,
  historyLoading: false,
  messages: [],
  askUserQuestion: () => {},
  saveMessage: () => {},
  clearHistory: () => {},
  setFeedbackProvided: () => {},
  onAskAQuestionPromptClick: () => {},
  maaBroadcastChannel: null,
});

const defaultSession = getDefaultSession();

// Create a provider component
/**
 * @param { AssistantProviderProps } props
 * @returns { React.Provider<AssistantContextProps> }
 */
export const AssistantProvider = ({ withPersistence, children }) => {
  const [session = defaultSession] = useLocalStorage('maa-session', defaultSession);
  const {
    messages,
    saveMessage,
    historyLoading,
    clearHistory,
    isSuggestedQuestionsLoading,
    wereQuestionsGenerated,
    setFeedbackProvided,
    broadCastMessageUpdate,
  } = useMessageHistory(withPersistence, session);
  const [
    submitUserQuestionToApi,
    { data: botResponse, loading: botLoading, error: botError },
  ] = useAssistantBot(session);
  const [historyCleared, setHistoryCleared] = useState(false);
  const maaBroadcastChannel = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.parent.LIFE_CYCLE_EVENT_BUS) {
      window.parent.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(
        'propagate-maa-llm',
        ({ output }) => {
          window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
            'CONFIG_SERVICE_CHANGE',
            output
          );
        });
    }
    return () => {
      window.parent.LIFE_CYCLE_EVENT_BUS.lifeCycle.off('propagate-maa-llm');
    };
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      maaBroadcastChannel.current = new BroadcastChannel('magic-apron-assistant');

      maaBroadcastChannel.current.onmessage = ({ data: { type }, origin }) => {
        if (type === 'MAA_RESET') {
          clearHistory({ origin });
          setHistoryCleared(true);
        }
      };
    }

    return () => {
      if (maaBroadcastChannel.current) {
        maaBroadcastChannel.current.close();
      }
    };
  }, [clearHistory]);

  const onAskAQuestionPromptClick = () => {
    // DEPRECATED: Use MAA_OPEN_DRAWER
    // TODO MAG4-426
    if (maaBroadcastChannel.current) {
      maaBroadcastChannel.current.postMessage({
        type: 'MAA_ASK_A_QUESTION',
      });
    }
  };

  // This condition gets hit when the response from the api can't save such as
  // when a user closes/changes tab while waiting for a response.
  // We want to persist the retry message in the db, but can't do it on page unload as that event is deprecated
  const saveRetryMessage = async () => {
    if (messages.at(-1).type === 'user') {
      return saveMessage(RETRY_MESSAGE);
    }
    return Promise.resolve();
  };

  const askUserQuestion = ({
    userQuestion,
    messageOrigin = MESSAGE_ORIGIN_UNSPECIFIED,
    suggestedQuestionPosition,
    isInitialSuggestedQuestion,
  }) => {
    // General question asked event.
    // Loader opens the drawer on this event.
    // DEPRECATED: Use MAA_OPEN_DRAWER
    // TODO MAG4-426
    if (maaBroadcastChannel.current) {
      maaBroadcastChannel.current.postMessage({
        type: 'MAA_USER_QUESTION',
        data: { userQuestion, messageOrigin },
      });
    }

    setHistoryCleared(false);
    saveRetryMessage()
      .then(() => saveMessage({ type: 'user', content: userQuestion }))
      .then((savedUserMessage) => {
        return submitUserQuestionToApi({
          userQuestion: savedUserMessage.content,
          timestamp: savedUserMessage.timestamp,
          messageOrigin,
          suggestedQuestionPosition,
          isInitialSuggestedQuestion,
        });
      })
      .then(() => {
        window.parent.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.question-ask', { userQuestion });
      })
      .catch(() => {
        nrTrack('MAA_MESSAGE_SAVE', { saveType: 'user_save_error' });
      });
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && window.parent.LIFE_CYCLE_EVENT_BUS) {
      window.parent.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(
        'magic-apron-assistant.ask-question',
        ({ output }) => {
          if (output.question) {
            askUserQuestion({
              userQuestion: output.question,
              messageOrigin: 'PREPOPULATED',
              suggestedQuestionPosition: output.position,
              isInitialSuggestedQuestion: true,
            });
          }
        }
      );
    }
    return () => {
      window.parent.LIFE_CYCLE_EVENT_BUS.lifeCycle.off(
        'magic-apron-assistant.ask-question');
    };
  }, [askUserQuestion]);

  // Waits for result of submitUserQuestionToApi in askUserQuestion before saving bot response
  // Unable to promise chain useLazyQuery trigger function from submitUserQuestionToApi so we wait for the useEffect
  useEffect(() => {
    if (!botLoading && (botResponse || botError)) {
      if (botError || !botResponse?.retrieveDetails?.responseSummary?.summaryAnswer) {
        nrTrack('MAA_MESSAGE', {
          message: 'BOT_ERROR_OOPS',
          parentExperience: getExperienceName(),
          parentURL: getParentPagePath()
        });
      }
      if (botResponse?.retrieveDetails?.additionalDetails?.inquiryDetails) {
        nrTrack('MAA_MESSAGE', {
          message: 'BOT_ERROR_OOPS_2',
          parentExperience: getExperienceName(),
          parentURL: getParentPagePath()
        });
      }
      const botContent = {
        type: 'bot',
        content: botError ? null : { ...(botResponse?.retrieveDetails || {}) },
        error: botError
      };
      saveMessage(botContent)
        .catch(() => {
          nrTrack('MAA_MESSAGE_SAVE', { saveType: 'bot_save_error' });
        })
        .finally(broadCastMessageUpdate);
    }
  }, [botLoading]);

  const context = {
    askUserQuestion,
    messages,
    saveMessage,
    historyLoading,
    isSuggestedQuestionsLoading,
    wereQuestionsGenerated,
    historyCleared,
    setFeedbackProvided,
    botLoading,
    maaBroadcastChannel,
    onAskAQuestionPromptClick,
    session,
  };

  return (
    <AssistantContext.Provider value={context}>
      {children}
    </AssistantContext.Provider>
  );
};

AssistantProvider.propTypes = {
  children: PropTypes.node.isRequired,
  withPersistence: PropTypes.bool,
};

AssistantProvider.defaultProps = {
  withPersistence: true,
};

// Custom hook to use the AssistantContext
/**
 * @returns { AssistantContextProps }
 */
export const useAssistant = () => {
  return useContext(AssistantContext);
};
